import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SalesInvoiceLineCreateDTO } from '../../interfaces/sales/salesInvoice.interface';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { SalesSettings } from '../../interfaces/sales/salesSettings.interface';
import { AccountService } from '../../services/account.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatMoney } from '../../utils/format';
import { helpWhy } from '../../help/why';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { leafAccounts } from '../../utils/postingAccounts';
import { SegmentService } from '../../services/segment.service';
import { SegmentSettingsService } from '../../services/segment-settings.service';
import { AuthService } from '../../services/auth.service';
import { CollectionsService } from '../../services/collections.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import SearchableSelect from '../common/SearchableSelect';
import DocumentCurrencyFields from '../common/DocumentCurrencyFields';
import { catalogueUnitPrice, isForeignCurrency, repriceCatalogueLines } from '../../utils/fcDocuments';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import SalesCatalogueSelect from './SalesCatalogueSelect';   // FW-2A
import { discountError, discountPatch, previewLine, shownDiscountAmount } from '../../utils/salesLineDiscount';   // FW-2A
import { hasTaxCodes, previewTax, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { TaxCodeLookup } from '../../interfaces/indirectTax.interface';
import ProjectSelect, { useCustomerProjectOptions } from '../project/ProjectSelect';   // P1-A tail

export const isInvoiceSegmentRequired = (revenueAccountId: string | undefined, accounts: Account[]): boolean =>
    !!revenueAccountId && accounts.find(a => a.id === revenueAccountId)?.segmentFieldPolicy === 'REQUIRED';

const today = () => toLocalIsoDate();

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// In-state row model: the DTO fields (quantity/unitPrice/taxRatePct may be NaN
// while the user is typing) plus a stable client-side key that is never sent
// to the server.
interface LineRow extends Omit<SalesInvoiceLineCreateDTO, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  // taxCodeId (from the DTO) is optional/nullable; when set, taxRatePct mirrors the code's effective rate.
  key: string;
  quantity: number;   // may be NaN while field is empty
  unitPrice: number;  // may be NaN while field is empty
  taxRatePct: number; // may be NaN while field is empty
  itemLabel?: string; // FW-2A: label of the catalogue item the line was picked from
  catalogueBdtPrice?: number | null; // polish 4: set while the price still comes from the catalogue pick
}

// Helpers that treat NaN as 0 for live-total computation only.
const safeLineNet = (row: LineRow): number => {
  return previewLine(row).net;   // FW-2A: net of the line discount (one shared preview, the server recomputes)
};
const safeLineTax = (row: LineRow): number => {
  // Coded line: tax is a rounded 2dp preview of net x effective rate (taxRatePct holds the code's rate).
  if (row.taxCodeId) return previewTax(safeLineNet(row), Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0);
  const t = Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0;
  return +((safeLineNet(row) * t) / 100).toFixed(4);
};

const SalesInvoiceCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Stable key counter — bumped each time a new row is created.
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);

  // Header state
  const [customerId, setCustomerId] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  // ---- B4-C: document currency ----
  const [currency, setCurrency] = useState('BDT');
  const [fxRate, setFxRate] = useState<number | ''>('');
  const foreign = isForeignCurrency(currency);
  // ---- end B4-C ----
  const [dueDate, setDueDate] = useState(today());
  const [notes, setNotes] = useState('');
  // ---- P1-A tail: header project (this customer's projects or ones with no customer) ----
  const [projectId, setProjectId] = useState('');
  const projectPick = useCustomerProjectOptions(customerId);
  useEffect(() => {
    // a customer change drops a project the new customer may not use
    if (projectPick.loaded && projectId && !projectPick.options.some(o => o.id === projectId)) setProjectId('');
  }, [projectPick, projectId]);
  // ---- end P1-A tail ----

  // Reference data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [revenueAccounts, setRevenueAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [settings, setSettings] = useState<SalesSettings | null>(null);
  const taxLookup = useTaxCodeLookup(AuthService.getSelectedInstitution());
  const showTaxCodes = hasTaxCodes(taxLookup);
  const defaultsApplied = useRef(false);
  const defaultRevenueAcct = revenueAccounts.find(a => a.id === settings?.defaultRevenueAccountId);
  const defaultRevenueLabel = defaultRevenueAcct ? `Default: ${defaultRevenueAcct.name}` : 'Choose an account';
  const [defaultSegmentId, setDefaultSegmentId] = useState<string>('');

  // Lines — stored as LineRow[] (includes transient key)
  const [lines, setLines] = useState<LineRow[]>([]);
  const [bulkPct, setBulkPct] = useState('');   // FW-2A
  const [lineKinds, setLineKinds] = useState<Record<string, 'STOCK' | 'SERVICE' | 'KIT'>>({});   // FW-2A: kind of each picked item (notice for stock items)

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Track whether user has manually changed due date

  const defaultCodeOf = (l: TaxCodeLookup | null) => {
    const id = l?.defaultOutputCodeId;
    const c = id ? l?.codes?.find(x => x.id === id && x.active) : undefined;
    return c ?? null;
  };

  const makeBlankLine = (s: SalesSettings | null): LineRow => {
    const dc = defaultCodeOf(taxLookup);
    return {
      key: nextKey(),
      description: '',
      quantity: 1,
      unitPrice: NaN,
      taxRatePct: dc ? dc.effectiveRatePct : (s?.defaultTaxRatePct ?? 0),
      taxCodeId: dc ? dc.id : null,
      revenueAccountId: undefined,
      segmentId: undefined,
    };
  };

  // The lookup can arrive after the first blank line was created: prefill it once with the institution default.
  useEffect(() => {
    if (loading || !taxLookup || defaultsApplied.current) return;
    defaultsApplied.current = true;
    const dc = defaultCodeOf(taxLookup);
    if (!dc) return;
    setLines(prev => prev.map(l => (l.taxCodeId ? l : { ...l, taxCodeId: dc.id, taxRatePct: dc.effectiveRatePct })));
  }, [loading, taxLookup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution()!;
    Promise.all([
      CustomerService.listByInstitution(institutionId, 0, 200),
      SalesSettingsService.getByInstitution(institutionId).catch(() => null),
      AccountService.getAllAccounts(),
      SegmentService.getByInstitution(0, 200),
      SegmentSettingsService.get().catch(() => null),
    ])
      .then(([customerPage, salesSettings, allAccounts, segmentPage, segSettings]) => {
        setCustomers(customerPage.content);
        setSettings(salesSettings);

        setRevenueAccounts(
          Array.isArray(allAccounts)
            ? allAccounts.filter(a => a.accountType === 'REVENUE')
            : []
        );

        const allSegments: Segment[] = segmentPage.content ?? [];
        setSegments(allSegments.filter(s => s.active !== false));

        setDefaultSegmentId(segSettings?.defaultSegmentId ?? '');

        // Set default due date from settings
        if (salesSettings?.defaultPaymentTermsDays) {
          setDueDate(addDays(today(), salesSettings.defaultPaymentTermsDays));
        }

        // Start with one blank line
        setLines([makeBlankLine(salesSettings)]);
      })
      .catch(e => toast.showApiError(e, 'Failed to load invoice data'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // W3-N: customer terms (else the settings default) drive the due date until the user edits it by hand.
  // Refs guard the async response: apply it only if the user has not edited the date and the customer/issue date
  // it was requested for are still the current ones.
  const dueTouchedRef = useRef(false);
  const customerRef = useRef('');
  const issueRef = useRef('');
  const localDefaultDue = (issue: string) =>
    settings?.defaultPaymentTermsDays ? addDays(issue, settings.defaultPaymentTermsDays) : issue;
  const prefillDueDate = (cust: string, issue: string) => {
    customerRef.current = cust;
    issueRef.current = issue;
    if (!cust || !issue) return;
    const stillCurrent = () => !dueTouchedRef.current && customerRef.current === cust && issueRef.current === issue;
    CollectionsService.defaultDueDate(cust, issue)
      .then(due => { if (stillCurrent()) setDueDate(due || localDefaultDue(issue)); })
      .catch(() => { if (stillCurrent()) setDueDate(localDefaultDue(issue)); });
  };

  const handleIssueDateChange = (newDate: string) => {
    setIssueDate(newDate);
    issueRef.current = newDate;
    if (!dueTouchedRef.current) {
      if (customerId) prefillDueDate(customerId, newDate);
      else setDueDate(localDefaultDue(newDate));
    }
  };

  const handleDueDateChange = (newDate: string) => {
    setDueDate(newDate);
    dueTouchedRef.current = true;
  };

  const handleAddLine = () => {
    setLines(prev => [...prev, makeBlankLine(settings)]);
  };

  const handleRemoveLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = <K extends keyof LineRow>(
    index: number,
    field: K,
    value: LineRow[K]
  ) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value,
        ...(field === 'unitPrice' ? { catalogueBdtPrice: null } : {}) };   // polish 4: a typed price stops catalogue re-pricing
      return updated;
    });
  };

  // Live totals — NaN fields treated as 0.
  const subtotal = lines.reduce((s, l) => s + safeLineNet(l), 0);
  const taxAmount = lines.reduce((s, l) => s + safeLineTax(l), 0);
  const total = subtotal + taxAmount;

  const isSubmitDisabled =
    saving ||
    !customerId ||
    lines.length === 0 ||
    lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0) || !!discountError(l));

  const submitWhy = saving ? null
    : !customerId ? 'Choose a customer first.'
    : lines.length === 0 ? 'Add at least one line.'
    : lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0)) ? 'Every line needs a quantity and a price above zero.'
    : lines.some(l => !!discountError(l)) ? 'A line discount must be between 0 and 100 percent and not more than the line amount.'
    : null;

  const handleSubmit = async () => {
    if (!customerId) { toast.showApiError(null, 'Select a customer'); return; }
    if (lines.length === 0) { toast.showApiError(null, 'Add at least one line'); return; }
    if (lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0))) {
      toast.showApiError(null, 'All lines must have a positive quantity and unit price');
      return;
    }
    if (lines.some(l => isInvoiceSegmentRequired(l.revenueAccountId, revenueAccounts) && !l.segmentId)) {
      toast.showApiError(null, 'Select a segment for every line whose revenue account requires one');
      return;
    }

    if (foreign && !(Number(fxRate) > 0)) { toast.showApiError(null, `Enter the ${currency} rate`); return; }

    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution()!;
      const dto = {
        customerId,
        issueDate,
        dueDate,
        notes: notes || undefined,
        institutionId,
        ...(foreign ? { currencyCode: currency, exchangeRate: Number(fxRate) } : {}),
        projectId: projectId || null,   // P1-A tail
        // Strip the transient key; coerce taxRatePct NaN→0 (qty/price are
        // finite because the submit guard above already checked > 0).
        lines: lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          taxCodeId: l.taxCodeId || null,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
          itemId: l.itemId || undefined,   // FW-2A
          discountPct: l.discountPct ?? undefined,
          discountAmount: l.discountPct == null ? (l.discountAmount ?? undefined) : undefined,
        })),
      };
      const created = await SalesInvoiceService.create(dto);
      toast.showSuccess('Sales Invoice created');
      navigate(`/sales/invoices/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create Sales Invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.invoiceCreate.page">
      <Typography variant="h5" sx={{ mb: 3 }}>New Sales Invoice</Typography>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }} data-help-section="sales.invoiceCreate.section.header">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SearchableSelect data-help="sales.invoiceCreate.field.customer"
            label="Customer"
            required
            options={customers}
            value={customerId}
            onChange={id => { setCustomerId(id); if (!dueTouchedRef.current) prefillDueDate(id, issueDate); else customerRef.current = id; }}
            getOptionLabel={c => c.name}
          />

          <ProjectSelect data-help="sales.invoiceCreate.field.project"
            label="Project (optional)"
            data-testid="inv-project"
            value={projectId}
            onChange={id => setProjectId(id || '')}
            options={projectPick.options}
            disabled={!customerId}
            placeholder={customerId ? '(no project)' : 'Choose a customer first'}
          />

          <DocumentCurrencyFields institutionId={AuthService.getSelectedInstitution() ?? ''} currency={currency}
            currencyProps={{ 'data-help': 'sales.invoiceCreate.field.currency' }} rateProps={{ 'data-help': 'sales.invoiceCreate.field.rate' }}
            rate={fxRate} asOf={issueDate} onChange={(c, r) => { setCurrency(c); setFxRate(r); setLines(prev => repriceCatalogueLines(prev, c, r)); }} />

          <TextField data-help="sales.invoiceCreate.field.issueDate"
            label="Issue Date"
            type="date"
            value={issueDate}
            onChange={e => handleIssueDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField data-help="sales.invoiceCreate.field.dueDate"
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => handleDueDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField data-help="sales.invoiceCreate.field.notes"
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />
        </Box>
      </Paper>

      {/* Lines */}
      <Paper sx={{ p: 3, mb: 3 }} data-help-section="sales.invoiceCreate.lines">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Invoice Lines</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {/* FW-2A: header discounts are out of scope; this just fills each line */}
            <TextField
              size="small" data-help="sales.invoiceCreate.bulkPct" label="Apply discount % to all lines" value={bulkPct}
              onChange={e => setBulkPct(e.target.value.replace(/[^0-9.]/g, ''))}
              inputProps={{ inputMode: 'decimal' }} sx={{ flex: '1 1 150px', maxWidth: 220 }}
            />
            <Button variant="outlined" data-help="sales.invoiceCreate.bulkApply" disabled={bulkPct === '' || Number(bulkPct) < 0 || Number(bulkPct) > 100}
              onClick={() => setLines(prev => prev.map(l => ({ ...l, discountPct: Number(bulkPct), discountAmount: null })))}>
              Apply
            </Button>
            <Button variant="outlined" data-help="sales.invoiceCreate.addLine" startIcon={<Add />} onClick={handleAddLine}>
              Add Line
            </Button>
          </Box>
        </Box>

        {lines.length === 0 ? (
          <Typography color="text.secondary">No lines added yet.</Typography>
        ) : (
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.invoiceCreate.col.product">Product / service</TableCell>
                    <TableCell data-help="sales.invoiceCreate.col.description">Description</TableCell>
                    <TableCell data-help="sales.invoiceCreate.col.qty">Quantity</TableCell>
                    <TableCell data-help="sales.invoiceCreate.col.unitPrice">{foreign ? `Unit Price (${currency})` : "Unit Price"}</TableCell>
                    <TableCell data-help="sales.invoiceCreate.col.discPct">Disc %</TableCell>
                    <TableCell data-help="sales.invoiceCreate.col.discAmount">Disc amount</TableCell>
                    {showTaxCodes && <TableCell data-help="sales.invoiceCreate.col.taxCode">Tax code</TableCell>}
                    <TableCell data-help="sales.invoiceCreate.col.taxPct">Tax %</TableCell>
                    <TableCell data-help="sales.invoiceCreate.col.revenue">Revenue Account</TableCell>
                    <TableCell data-help="sales.invoiceCreate.col.segment">Segment</TableCell>
                    <TableCell align="right" data-help="sales.invoiceCreate.col.net">Net</TableCell>
                    <TableCell align="right" data-help="sales.invoiceCreate.col.tax">Tax</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={line.key}>
                      <TableCell sx={{ minWidth: 190 }}>
                        <SalesCatalogueSelect data-help="sales.invoiceCreate.col.product"
                          label="Product / service (optional)"
                          value={line.itemId ?? null}
                          valueLabel={line.itemLabel}
                          onChange={(id, item) => { if (id && item) setLineKinds(k => ({ ...k, [id]: item.kind }));
                            setLines(prev => prev.map((l, i) => (i !== index ? l : !id || !item
                            ? { ...l, itemId: null, itemLabel: undefined, catalogueBdtPrice: null }
                            : {
                              ...l,
                              itemId: id,
                              itemLabel: `${item.code} - ${item.name}`,
                              description: item.name,
                              unitPrice: item.salePrice != null ? catalogueUnitPrice(item.salePrice, currency, fxRate) : l.unitPrice,
                              catalogueBdtPrice: item.salePrice != null ? item.salePrice : null,   // polish 4
                              revenueAccountId: item.revenueAccountId || l.revenueAccountId,
                              ...(item.taxCodeId && taxLookup?.codes?.some(c => c.id === item.taxCodeId && c.active)
                                ? { taxCodeId: item.taxCodeId, taxRatePct: taxLookup.codes.find(c => c.id === item.taxCodeId)!.effectiveRatePct }
                                : {}),
                            }))); }}
                        />
                        {line.itemId && lineKinds[line.itemId] !== 'SERVICE' && !!lineKinds[line.itemId] &&   /* F6c: kits too */ (
                          <Typography variant="caption" color="warning.main" display="block">
                            Stock is not relieved by an invoice; use a sales order and delivery challan.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceCreate.col.description"
                          size="small"
                          value={line.description}
                          onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Description"
                          sx={{ minWidth: 160 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceCreate.col.qty"
                          type="number"
                          size="small"
                          value={Number.isFinite(line.quantity) ? line.quantity : ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleLineChange(index, 'quantity', v === '' ? NaN : Number(v));
                          }}
                          error={!(line.quantity > 0)}
                          inputProps={{ step: 'any', min: 0 }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceCreate.col.unitPrice"
                          type="number"
                          size="small"
                          value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleLineChange(index, 'unitPrice', v === '' ? NaN : Number(v));
                          }}
                          error={!(line.unitPrice > 0)}
                          inputProps={{ step: 'any', min: 0 }}
                          sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceCreate.col.discPct"
                          type="number" size="small"
                          inputProps={{ 'aria-label': 'Disc %', step: 'any', min: 0, max: 100 }}
                          value={line.discountPct ?? ''}
                          onChange={e => setLines(prev => prev.map((l, i) => (i !== index ? l : { ...l, ...discountPatch('pct', e.target.value) })))}
                          error={!!discountError(line)}
                          helperText={discountError(line) ?? undefined}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceCreate.col.discAmount"
                          type="number" size="small"
                          inputProps={{ 'aria-label': 'Disc amount', step: 'any', min: 0 }}
                          value={shownDiscountAmount(line)}
                          onChange={e => setLines(prev => prev.map((l, i) => (i !== index ? l : { ...l, ...discountPatch('amount', e.target.value) })))}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      {showTaxCodes && (
                        <TableCell>
                          <TaxCodeSelect data-help="sales.invoiceCreate.col.taxCode"
                            data-testid={`inv-taxcode-${index}`}
                            lookup={taxLookup}
                            side="OUTPUT"
                            value={line.taxCodeId}
                            onChange={(id, item) => setLines(prev => prev.map((l, i) => (i !== index ? l : {
                              ...l,
                              taxCodeId: id,
                              // Selecting a code shows its effective rate; clearing returns to the free rate.
                              taxRatePct: item ? item.effectiveRatePct : (id ? l.taxRatePct : (settings?.defaultTaxRatePct ?? 0)),
                            })))}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField data-help="sales.invoiceCreate.col.taxPct"
                          type="number"
                          size="small"
                          disabled={!!line.taxCodeId}
                          inputProps={{ 'aria-label': 'Tax %', step: 0.01, min: 0 }}
                          value={Number.isFinite(line.taxRatePct) ? line.taxRatePct : ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleLineChange(index, 'taxRatePct', v === '' ? NaN : Number(v));
                          }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 180 }}>
                          <AccountAutocomplete data-help="sales.invoiceCreate.col.revenue"
                            data-testid={`inv-revenue-${index}`}
                            accounts={leafAccounts(revenueAccounts)}
                            value={line.revenueAccountId ?? ''}
                            label="Revenue Account"
                            placeholder={defaultRevenueLabel}
                            onChange={accountId => {
                              handleLineChange(index, 'revenueAccountId', accountId || undefined);
                              if (isInvoiceSegmentRequired(accountId || undefined, revenueAccounts) && !line.segmentId && defaultSegmentId) {
                                handleLineChange(index, 'segmentId', defaultSegmentId);
                              }
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SearchableSelect data-help="sales.invoiceCreate.col.segment"
                            label="Segment"
                            size="small"
                            sx={{ minWidth: 160 }}
                            error={isInvoiceSegmentRequired(line.revenueAccountId, revenueAccounts) && !line.segmentId}
                            data-testid={`inv-segment-${index}`}
                            options={segments}
                            value={line.segmentId ?? ''}
                            onChange={val => handleLineChange(index, 'segmentId', val || undefined)}
                            getOptionLabel={s => `${s.code} — ${s.name}`}
                            placeholder="—"
                          />
                      </TableCell>
                      <TableCell align="right">{formatMoney(safeLineNet(line))}</TableCell>
                      <TableCell align="right">{formatMoney(safeLineTax(line))}</TableCell>
                      <TableCell>
                        <IconButton
                          data-help="sales.invoice.lines.remove"
                          size="small"
                          color="error"
                          onClick={() => handleRemoveLine(index)}
                          aria-label="remove line"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        )}
      </Paper>

      {/* Totals */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body1">{formatMoney(subtotal)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body1">{formatMoney(taxAmount)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h6">{formatMoney(total)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          data-help="sales.invoiceCreate.save"
          {...helpWhy(submitWhy ? { kind: 'status', message: submitWhy } : null)}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {saving ? <CircularProgress size={20} /> : 'Create Invoice'}
        </Button>
        <Button variant="outlined" data-help="sales.invoiceCreate.cancel" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SalesInvoiceCreate;
